/**
 * Footer - Premium corporate typographic footer
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaLinkedinIn, FaFacebookF, FaInstagram, FaYoutube,
  FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe,
  FaArrowRight, FaFemale
} from 'react-icons/fa';
import AnimatedSection from '../ui/AnimatedSection';
import logoImg from '../../assets/images/logo.png';

const quickLinks = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Services', path: '/services' },
  { label: 'Contact', path: '/contact' }
];

const serviceLinks = [
  { label: 'Contract Staffing & Manpower', path: '/services#contract-staffing' },
  { label: 'Shipping & Marine', path: '/services#shipping-marine' },
  { label: 'Project Cargo', path: '/services#logistics-project' },
  { label: 'Integrated Logistics', path: '/services#integrated-logistics' },
  { label: 'Green Energy Solutions', path: '/services#green-energy' }
];

const socialLinks = [
  { icon: FaLinkedinIn, label: 'LinkedIn', url: 'https://linkedin.com' },
  { icon: FaFacebookF, label: 'Facebook', url: 'https://facebook.com' },
  { icon: FaInstagram, label: 'Instagram', url: 'https://instagram.com' },
  { icon: FaYoutube, label: 'YouTube', url: 'https://youtube.com' }
];

const Footer = () => {
  return (
    <footer style={{
      background: 'var(--navy-deep)',
      color: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden',
      borderTop: '1px solid var(--orange-border)'
    }}>

      {/* Main footer content */}
      <div className="container-xxl px-4 px-lg-5 py-5">
        <div className="row g-5">

          {/* Column 1 - Company Info */}
          <div className="col-lg-5 col-md-6">
            <AnimatedSection variant="fadeUp" delay={0}>
              <Link to="/" className="d-inline-flex mb-4" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.9rem' }}>
                <img
                  src={logoImg}
                  alt="ISAAC Logistic Solutions India Pvt. Ltd. Logo"
                  style={{
                    height: 52,
                    width: 'auto',
                    objectFit: 'contain',
                    display: 'block'
                  }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{
                    fontFamily: 'var(--font-heading)',
                    fontWeight: 800,
                    fontSize: 'clamp(0.85rem, 1.5vw, 1.15rem)',
                    color: '#fff',
                    letterSpacing: '0.5px',
                    whiteSpace: 'nowrap'
                  }}>
                    {/* Shortened brand name on mobile / tablet */}
                    <span className="d-inline d-md-none">ISAAC Logistics</span>
                    {/* Full legal name on desktop */}
                    <span className="d-none d-md-inline">ISAAC Logistic Solutions India Pvt. Ltd.</span>
                  </span>
                </div>
              </Link>

              <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.85rem', lineHeight: '1.8', marginBottom: '1.5rem', textAlign: 'justify' }}>
                ISAAC Logistic Solutions India Pvt. Ltd. — a women lead organisation delivering integrated shipping, logistics and marine solutions with excellence, integrity and global standards.
              </p>

              {/* Tagline & Corporate Status badges */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
                <div style={{
                  display: 'inline-block',
                  background: 'var(--orange-dim)',
                  border: '1px solid var(--orange-border)',
                  borderRadius: 2,
                  padding: '0.35rem 0.85rem',
                  fontSize: '0.68rem',
                  color: 'var(--orange)',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-heading)',
                }}>
                  One Partner. Endless Solutions.
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  background: 'var(--purple-accent-dim)',
                  border: '1px solid var(--purple-accent-border)',
                  borderRadius: 2,
                  padding: '0.35rem 0.85rem',
                  fontSize: '0.68rem',
                  color: '#fff',
                  fontWeight: 600,
                  letterSpacing: '1px',
                  textTransform: 'uppercase',
                  fontFamily: 'var(--font-heading)',
                }}>
                  <FaFemale size={10} color="var(--purple-accent-light)" />
                  Women Empowered Entity
                </div>
              </div>

              {/* Social icons */}
              <div className="d-flex gap-2">
                {socialLinks.map((s, i) => (
                  <motion.a
                    key={i} href={s.url} target="_blank" rel="noopener noreferrer"
                    aria-label={s.label}
                    whileHover={{ y: -2 }}
                    style={{
                      width: '32px', height: '32px', borderRadius: 2,
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(255,255,255,0.08)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'rgba(255,255,255,0.6)', transition: 'all 0.2s ease'
                    }}
                    className="footer-social-icon"
                  >
                    <s.icon size={12} />
                  </motion.a>
                ))}
              </div>
            </AnimatedSection>
          </div>

          {/* Column 2 - Quick Links */}
          <div className="col-lg-2 col-md-6 col-6">
            <AnimatedSection variant="fadeUp" delay={0.08}>
              <h5 style={{
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                color: '#fff', fontSize: '0.85rem', marginBottom: '1.5rem',
                textTransform: 'uppercase', letterSpacing: '1px'
              }}>
                Quick Links
              </h5>
              <div style={{ width: '20px', height: '1px', background: 'var(--orange)', marginBottom: '1.2rem' }} />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {quickLinks.map((l, i) => (
                  <li key={i} style={{ marginBottom: '0.6rem' }}>
                    <Link to={l.path} style={{
                      color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem',
                      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem',
                      transition: 'color 0.2s ease'
                    }}
                      className="footer-link"
                    >
                      <FaArrowRight size={8} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>

          {/* Column 3 - Services */}
          <div className="col-lg-2 col-md-6 col-6">
            <AnimatedSection variant="fadeUp" delay={0.12}>
              <h5 style={{
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                color: '#fff', fontSize: '0.85rem', marginBottom: '1.5rem',
                textTransform: 'uppercase', letterSpacing: '1px'
              }}>
                Services
              </h5>
              <div style={{ width: '20px', height: '1px', background: 'var(--orange)', marginBottom: '1.2rem' }} />
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {serviceLinks.map((l, i) => (
                  <li key={i} style={{ marginBottom: '0.6rem' }}>
                    <Link to={l.path} style={{
                      color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem',
                      textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '0.4rem',
                      transition: 'color 0.2s ease'
                    }}
                      className="footer-link"
                    >
                      <FaArrowRight size={8} style={{ color: 'var(--orange)', flexShrink: 0 }} />
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </AnimatedSection>
          </div>

          {/* Column 4 - Contact */}
          <div className="col-lg-3 col-md-6">
            <AnimatedSection variant="fadeUp" delay={0.16}>
              <h5 style={{
                fontFamily: 'var(--font-heading)', fontWeight: 700,
                color: '#fff', fontSize: '0.85rem', marginBottom: '1.5rem',
                textTransform: 'uppercase', letterSpacing: '1px'
              }}>
                Contact Us
              </h5>
              <div style={{ width: '20px', height: '1px', background: 'var(--orange)', marginBottom: '1.2rem' }} />

              <div className="d-flex flex-column gap-3">
                <div className="d-flex gap-3 align-items-start">
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 2, flexShrink: 0,
                    background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px'
                  }}>
                    <FaMapMarkerAlt size={11} color="var(--orange)" />
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.82rem', lineHeight: '1.65', margin: 0, textAlign: 'justify' }}>
                    SF. No.156/1, Plot 3/436-66, Isaac Street,<br />
                    Central State Govt Employee Colony, Kumaragiri,<br />
                    Pudukkottai, Thoothukudi - 628103,<br />
                    Tamil Nadu, India.
                  </p>
                </div>

                <div className="d-flex gap-3 align-items-center">
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 2, flexShrink: 0,
                    background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <FaPhone size={11} color="var(--orange)" />
                  </div>
                  <a href="tel:+917010755885" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', textDecoration: 'none' }} className="footer-link">
                    +91 70107 55885
                  </a>
                </div>

                <div className="d-flex gap-3 align-items-center">
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 2, flexShrink: 0,
                    background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <FaEnvelope size={11} color="var(--orange)" />
                  </div>
                  <a href="mailto:info@isaacgroup.net" style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', textDecoration: 'none' }} className="footer-link">
                    info@isaacgroup.net
                  </a>
                </div>

                <div className="d-flex gap-3 align-items-center">
                  <div style={{
                    width: '28px', height: '28px', borderRadius: 2, flexShrink: 0,
                    background: 'var(--orange-dim)', border: '1px solid var(--orange-border)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <FaGlobe size={11} color="var(--orange)" />
                  </div>
                  <a href="https://www.isaacgroup.net" target="_blank" rel="noopener noreferrer"
                    style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem', textDecoration: 'none' }} className="footer-link">
                    www.isaacgroup.net
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div style={{ borderTop: '1px solid rgba(255,255,255,0.05)', padding: '1.5rem 0' }}>
        <div className="container-xxl px-4 px-lg-5">
          <div className="d-flex flex-column flex-md-row align-items-center justify-content-between gap-2">
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', margin: 0 }}>
              © {new Date().getFullYear()} ISAAC Logistic Solutions India Pvt. Ltd. All Rights Reserved.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', margin: 0 }}>
              Powered by <span style={{ color: 'var(--orange)' }}>Logic Technologies</span> - Tuticorin - <a href="tel:+919843937930" style={{ color: 'rgba(255,255,255,0.4)', textDecoration: 'none' }} className="footer-link">98439 37930</a>
            </p>
          </div>
        </div>
      </div>

      <style>{`
        .footer-link:hover { color: var(--orange) !important; }
        .footer-social-icon:hover { color: var(--orange) !important; border-color: var(--orange-border) !important; background: rgba(255,255,255,0.06) !important; }
      `}</style>
    </footer>
  );
};

export default Footer;
